/**
 * V7Vista CRM Integration — Nausiddhi Realty
 * API docs: https://v7vista.tranquilcrmone.in/apidoc/3
 */

const CRM_ENDPOINT = 'https://v7vista.tranquilcrmone.in/v2/createlead';
const API_KEY      = 'TRNQUILCRMnausiddhi';
const SOURCE_TYPE  = '3';

/**
 * CRM project registry.
 * `slug` matches the React Router param used in ProjectDetailPage
 * (i.e. the id in projectsStore). `aliases` cover legacy/alternate
 * spellings so older links and admin-typed names still resolve.
 *
 * NOTE: Dapoli (Seaview Enclave) has no CRM project id yet — it still
 * resolves to 4 under the hood (same bucket as the generic "Talk to
 * experts" fallback) until the client provisions a real id for it in
 * V7Vista. It now gets its own labeled dropdown entry so it doesn't look
 * missing from the enquiry form, but both entries currently land in the
 * same CRM bucket. Give Seaview its own `id` here once V7Vista has one.
 */
export const CRM_PROJECTS = [
  { id: 4, name: 'Talk to experts',       slug: 'any',
    aliases: [] },
  { id: 1, name: 'BlueRain Resort Homes', slug: 'blue-rain-resort-homes',
    aliases: ['bluerain-resort-homes', 'blue rain resort homes'] },
  { id: 2, name: 'Sai Shrushti',          slug: 'sai-shrusti',
    aliases: ['sai-srushti', 'sai srushti', 'sai shrushti', 'sai shrusti'] },
  { id: 3, name: 'Nausiddhi Residence',   slug: 'nausiddhi-residency',
    aliases: ['nausiddhi-residence', 'nausiddhi residency'] },
  // Shares id 4 with "Talk to experts" — see NOTE above.
  { id: 4, name: 'Seaview Enclave',       slug: 'nausiddhi-coastal-greens',
    aliases: ['coastal-greens', 'dapoli', 'seaview-enclaves', 'seaview', 'seaview enclaves', 'seaview-enclave', 'seacleave-enclaves', 'seacleave', 'seacleave enclaves'] },
];

/**
 * Resolve a route slug, project name, or raw numeric string → CRM project ID.
 * Falls back to 4 ("Any Project") when no match is found.
 */
export function resolveCRMProjectId(slugOrName) {
  if (!slugOrName) return 4;
  const s = String(slugOrName).toLowerCase().trim();
  const match = CRM_PROJECTS.find(
    (p) =>
      p.slug === s ||
      p.name.toLowerCase() === s ||
      String(p.id) === s ||
      (p.aliases && p.aliases.includes(s))
  );
  return match ? match.id : 4;
}

/** Pull UTM / gclid params from the current URL. */
function getUTMParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      campaign_name: p.get('utm_campaign') || undefined,
      adgroup_name:  p.get('utm_medium')   || undefined,
      ad_name:       p.get('utm_content')  || undefined,
      gcl_id:        p.get('gclid')        || undefined,
      sub_source:    p.get('utm_source')   || 'WebForm',
    };
  } catch {
    return { sub_source: 'WebForm' };
  }
}

/**
 * Submit a lead to the CRM.
 *
 * @param {Object}  lead
 * @param {string}  lead.phone       – 10-digit mobile (no country code)
 * @param {number}  lead.projectId   – CRM project ID 1–4
 * @param {string} [lead.name]
 * @param {string} [lead.email]
 * @param {string} [lead.remark]
 * @param {string} [lead.minBudget]  – e.g. "20L"
 * @param {string} [lead.budget]     – e.g. "80L"
 * @param {string} [lead.leadType]   – "rental" | "resale" | "sales"
 * @param {string} [lead.subSource]  – overrides the UTM-derived sub_source.
 *                                     Used by the Channel Partner page so CP
 *                                     submissions are distinguishable from
 *                                     ordinary buyer enquiries in the CRM.
 *
 * @returns {Promise<{ status: boolean, msg: string }>}
 */
export async function submitLead(lead) {
  const utm = getUTMParams();

  const params = new URLSearchParams();
  params.set('api_key',         API_KEY);
  params.set('country_code',    '91');
  params.set('mobile_number',   lead.phone.replace(/\D/g, ''));
  params.set('project_id',      String(lead.projectId ?? 4));
  params.set('project_id_type', 'id');
  params.set('source_type',     SOURCE_TYPE);
  // An explicit subSource (e.g. the CP portal) wins over the UTM value —
  // otherwise a partner arriving via an ad campaign would be filed under
  // that campaign rather than as a channel-partner submission.
  params.set('sub_source',      lead.subSource || utm.sub_source || 'WebForm');

  if (lead.name)             params.set('customer_name',  lead.name.trim());
  if (lead.email)            params.set('email',          lead.email.trim());
  if (lead.remark)           params.set('remark',         lead.remark.trim());
  if (lead.minBudget)        params.set('min_budget',     lead.minBudget);
  if (lead.budget)           params.set('budget',         lead.budget);
  if (lead.leadType)         params.set('lead_type',      lead.leadType);
  if (utm.campaign_name)     params.set('campaign_name',  utm.campaign_name);
  if (utm.adgroup_name)      params.set('adgroup_name',   utm.adgroup_name);
  if (utm.ad_name)           params.set('ad_name',        utm.ad_name);
  if (utm.gcl_id)            params.set('gcl_id',         utm.gcl_id);

  const response = await fetch(`${CRM_ENDPOINT}?${params.toString()}`);
  if (!response.ok) throw new Error(`CRM HTTP ${response.status}`);

  const data = await response.json();
  // Both "Lead punched" and "Duplicate" are treated as success — the lead exists.
  return data;
}

/* ──────────────────────────────────────────────────────────────────────────
   CHANNEL PARTNER (CP) SUBMISSIONS
   --------------------------------------------------------------------------
   V7Vista is building a full CP Portal (partner logins, lead-ownership
   tracking, commission statements). Until that exists there is no CP-specific
   endpoint to call, and this site has no backend of its own — so both CP
   flows below reuse the SAME createlead API as buyer enquiries, tagged so the
   CRM team can tell them apart:

     sub_source  → 'ChannelPartner'
     remark      → prefixed with [CHANNEL PARTNER REGISTRATION] or
                   [CP LEAD - via <partner name> <phone>]

   When V7Vista ships a real CP endpoint/param, only these two functions need
   to change — the page components call them and nothing else.
   ────────────────────────────────────────────────────────────────────────── */

export const CP_SUB_SOURCE = 'ChannelPartner';

/**
 * A broker/agent applying to become an empanelled channel partner.
 * Their own details are the "lead" — the CRM team follows up to onboard them.
 */
export async function submitPartnerRegistration({
  name, phone, email, firm, city, message,
}) {
  const details = [
    firm && `Firm: ${firm}`,
    city && `City: ${city}`,
    message && `Note: ${message}`,
  ].filter(Boolean).join(' | ');

  return submitLead({
    name,
    phone,
    email,
    projectId: 4,          // "Talk to experts" — a registration isn't project-specific
    leadType:  'sales',
    subSource: CP_SUB_SOURCE,
    remark:    `[CHANNEL PARTNER REGISTRATION] ${details}`.trim(),
  });
}

/**
 * An empanelled partner registering one of THEIR clients against a project.
 * The client is the lead; the partner is recorded in the remark so V7Vista
 * can attribute the lead (and eventually the commission) to them.
 */
export async function submitPartnerLead({
  clientName, clientPhone, clientEmail, projectId,
  partnerName, partnerPhone, minBudget, budget, message,
}) {
  const attribution = [
    `via ${partnerName}`,
    partnerPhone && `(${partnerPhone})`,
  ].filter(Boolean).join(' ');

  const note = message ? ` | Note: ${message}` : '';

  return submitLead({
    name:      clientName,
    phone:     clientPhone,
    email:     clientEmail,
    projectId,
    minBudget,
    budget,
    leadType:  'sales',
    subSource: CP_SUB_SOURCE,
    remark:    `[CP LEAD - ${attribution}]${note}`,
  });
}
